export const DEFAULT_REDIRECT = '/dashboard';

/**
 * Normalises the `redirect` query parameter used by the login / license flows.
 *
 * Returns null when there is nothing usable to redirect to. Callers that
 * serialize the value back into a query string should skip the parameter
 * entirely when this returns null - emitting `redirect=undefined` produces the
 * truthy string "undefined", which then survives a `|| '/dashboard'` fallback
 * and gets pushed as a route.
 *
 * Only same-origin absolute paths are accepted, so a crafted login URL cannot
 * bounce the user off to another host after authenticating.
 */
export function sanitizeRedirect(redirect) {
  if (typeof redirect !== 'string') {
    return null;
  }

  const value = redirect.trim();
  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }

  // Must be an in-app path. `//host` and `/\host` are protocol-relative URLs.
  if (!value.startsWith('/') || /^\/[/\\]/.test(value)) {
    return null;
  }

  return value;
}

/**
 * Same as sanitizeRedirect, but falls back to the dashboard so the result can
 * be handed straight to router.push().
 */
export function redirectTarget(redirect) {
  return sanitizeRedirect(redirect) ?? DEFAULT_REDIRECT;
}
